import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getAddressHistory } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, History, Download } from "lucide-react";

const formatFMI = (wei) => (Number(wei) / 1e18).toFixed(4);
const truncate = (s, a = 8, b = 6) => (!s || s.length <= a + b ? s : `${s.slice(0, a)}...${s.slice(-b)}`);

const TxRow = ({ tx, address }) => {
  const me = address.toLowerCase();
  const tt = tx.tokenTransfer;
  const isOut = tx.from?.toLowerCase() === me;
  const counterparty = isOut ? (tt?.to || tx.to) : tx.from;
  return (
    <TableRow className="border-[#2A2411]">
      <TableCell><Link to={`/tx/${tx.hash}`} className="table-link font-mono text-xs">{truncate(tx.hash)}</Link></TableCell>
      <TableCell><Link to={`/block/${tx.blockNumber}`} className="table-link font-mono text-xs">{tx.blockNumber?.toLocaleString()}</Link></TableCell>
      <TableCell>
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${isOut ? "bg-red-500/10 text-red-400" : "bg-green-500/10 text-green-400"}`}>
          {isOut ? "OUT" : "IN"}
        </span>
      </TableCell>
      <TableCell><Link to={`/address/${counterparty}`} className="table-link font-mono text-xs">{truncate(counterparty, 6, 4)}</Link></TableCell>
      <TableCell className="text-right text-zinc-300 font-mono text-xs">{tt ? `${tt.amount.toLocaleString()} ${tt.token}` : `${formatFMI(tx.value)} FMI`}</TableCell>
    </TableRow>
  );
};

const exportCsv = (address, txs) => {
  const header = ["Tx Hash", "Block", "Timestamp (UTC)", "Direction", "From", "To", "Amount", "Gas Price (Gwei)"];
  const rows = txs.map((t) => [
    t.hash, t.blockNumber, t.timestamp ? new Date(t.timestamp * 1000).toISOString() : "",
    t.from?.toLowerCase() === address.toLowerCase() ? "OUT" : "IN",
    t.from, t.tokenTransfer?.to || t.to,
    t.tokenTransfer ? `${t.tokenTransfer.amount} ${t.tokenTransfer.token}` : `${Number(t.value) / 1e18} FMI`,
    (Number(t.gasPrice) / 1e9).toString(),
  ]);
  const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `fsc-${address.slice(0, 10)}-transactions.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

export const AddressHistory = ({ address }) => {
  const { data, fetchNextPage, hasNextPage, isFetching, isError } = useInfiniteQuery({
    queryKey: ["addressHistory", address],
    queryFn: ({ pageParam }) => getAddressHistory(address, pageParam),
    initialPageParam: null,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
  });
  const pages = data?.pages || [];
  const txs = pages.flatMap((p) => p.transactions);
  const last = pages[pages.length - 1];

  return (
    <Card data-testid="address-history" className="border-[#2A2411] bg-[#0A0A0A] mt-6">
      <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <History size={16} className="text-[#D4AF37]" /> Transaction History
        </CardTitle>
        <div className="flex items-center gap-3">
          {last && (
            <span data-testid="address-history-range" className="text-xs text-zinc-500 font-mono hidden sm:inline">
              Scanned blocks {last.scannedFrom.toLocaleString()} – {pages[0].scannedTo.toLocaleString()}
            </span>
          )}
          <Button data-testid="address-history-export-csv" size="sm" variant="outline" disabled={!txs.length}
            onClick={() => exportCsv(address, txs)}
            className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F3E5AB] h-8">
            <Download size={14} className="mr-1.5" /> Export CSV{txs.length ? ` (${txs.length})` : ""}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {txs.length > 0 ? (
          <Table data-testid="address-history-table">
            <TableHeader>
              <TableRow className="border-[#2A2411] hover:bg-transparent">
                {["Tx Hash", "Block", "Dir", "Counterparty"].map((h) => <TableHead key={h} className="text-zinc-500 text-xs">{h}</TableHead>)}
                <TableHead className="text-zinc-500 text-xs text-right">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{txs.map((tx, i) => <TxRow key={tx.hash + i} tx={tx} address={address} />)}</TableBody>
          </Table>
        ) : (
          !isFetching && <p data-testid="address-history-empty" className="text-sm text-zinc-500 px-6 py-6">No transactions found in the scanned blocks yet.</p>
        )}
        {isError && <p className="text-sm text-red-400 px-6 py-3">Could not load history from the chain.</p>}
        <div className="p-4 flex justify-center">
          {hasNextPage && (
            <Button data-testid="address-history-load-more" variant="outline" disabled={isFetching} onClick={() => fetchNextPage()}
              className="border-[#2A2411] text-zinc-300 hover:text-white hover:bg-[#D4AF37]/10">
              {isFetching ? <><Loader2 size={14} className="animate-spin mr-2" /> Scanning...</> : "Load older transactions"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
